import { ChevronDown, RotateCcw } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import type { JourneyStage, LabOrderStatus, AppStatus, ClaimsInsightsState } from '../../models/careOSPrototype';
import { APP_STATUS_ORDER, APP_STATUS_META } from '../../models/careOSPrototype';
import type { HomeState, RosterState } from './PrototypeStateContext';
import { PROTOTYPE_BAR_ENABLED, usePrototypeState } from './PrototypeStateContext';

/**
 * Expandable prototype state controls, rendered in the workspace chrome
 * directly under `GlobalBar` (see `Root`). The disclosure toggle that opens
 * and closes this row lives in `GlobalBar` itself, so the two read shared
 * state from `PrototypeStateContext`.
 *
 * Why it lives up here rather than inside the patient profile: as a row in the
 * clinical frame it visually collided with the SideNav, and it only rendered on
 * routes that had prototype state — so the app shifted vertically as you
 * navigated. In the workspace chrome it sits above the whole app on every
 * route, and the only thing that moves the layout is the presenter explicitly
 * expanding or collapsing it. Collapsed, it takes zero height, so a
 * stakeholder demo shows the prototype with just the one workspace bar.
 *
 * The bar shows only the controls whose effect is visible on the current
 * surface (see `CONTROLS_BY_SURFACE`) — a dropdown you can't see the result of
 * is just noise, and worse, invites changing a state and concluding nothing
 * happened.
 */

// Matches GlobalBar's height and background so the two read as one stack of
// workspace chrome rather than two competing bars.
const BAR_HEIGHT = 34;
const BAR_BG = '#02191C';

// Type scale is pinned to literals rather than the `--text-*` custom properties
// the careOS components use: those are scoped to `.careos-root`, and this bar
// deliberately renders outside it, so the vars resolve to nothing and the
// controls fall back to the UA default (bigger and lighter than intended).
// 12px/600 is the same scale as the GlobalBar's "Prototype" label.
const VALUE_FONT_SIZE = 12;
const VALUE_FONT_WEIGHT = 600;
const LABEL_FONT_SIZE = 10;
const LABEL_FONT_WEIGHT = 700;

// Labs scenarios, in journey order: nothing staged → task routed here → results
// back and awaiting a read → read done. Each one is a page shape a presenter
// can't otherwise produce (see `JourneyStage` for what each renders), which is
// the bar's whole reason for carrying this control.
//
// There's no "Form" entry. Opening the new-order form is what the "+ New Lab
// Order" button on the tab header does, so it isn't a state to stage — and as a
// dropdown value it rendered the identical form to "Review", leaving the
// dropdown reporting a stage that wasn't the one on screen.
const STAGES: { value: JourneyStage; label: string }[] = [
  // Labelled "Steady State" — from the labs workflow's point of view the
  // 'profile' stage is the resting state, with nothing staged over it. The
  // underlying `JourneyStage` value stays 'profile'; it's threaded through
  // LabsTab and the rest of the prototype.
  { value: 'profile', label: 'Steady State' },
  { value: 'alert', label: 'Alert' },
  { value: 'review', label: 'Review' },
  { value: 'done', label: 'Done' },
];

// App-onboarding states for the Gut Checks card status pill.
const APP_STATUSES: { value: AppStatus; label: string }[] = APP_STATUS_ORDER.map(
  value => ({ value, label: APP_STATUS_META[value].label })
);

// Demo scenarios for the Claims Insights tab (Claims Insights PRD §4). Stands
// in for a member's real first-visit/claims-feed status — see the fixture
// comment in mocks/fixtures/careOSPrototype.ts.
const CLAIMS_STATES: { value: ClaimsInsightsState; label: string }[] = [
  { value: 'before-first-visit', label: 'Before First Visit' },
  { value: 'summary-ready', label: 'Summary + Claims' },
  { value: 'steady-state', label: 'Steady State' },
  { value: 'no-claims-data', label: 'No Claims Data' },
];

// Labs is a single axis from "no order placed" through the two terminal
// ranges. Intermediate lifecycle states (pending-collection, processing,
// pending-review) aren't reachable through the UI, so the bar omits them:
// orders stay Active until "Mark as Read" resolves them to a range. Picking a
// status with no order synthesizes one; "No active order" clears it.
//
// Stage writes to this same order (see `orderForStage`), so the two dropdowns
// stay in agreement: staging a scenario updates what Labs reads, and setting
// Labs by hand drops Stage back to Steady State.
const LAB_STATUSES: { value: LabOrderStatus | 'none'; label: string }[] = [
  { value: 'none', label: 'No active order' },
  { value: 'submitted', label: 'Active' },
  { value: 'completed', label: 'In Range' },
  { value: 'out-of-range', label: 'Out of Range' },
];

const HOME_STATES: { value: HomeState; label: string }[] = [
  { value: 'filled', label: 'Filled' },
  { value: 'empty', label: 'Empty' },
];

const ROSTER_STATES: { value: RosterState; label: string }[] = [
  { value: 'mixed', label: 'Mixed panel' },
  { value: 'needs-outreach', label: 'Needs outreach' },
  { value: 'empty', label: 'Empty panel' },
];

/**
 * The surface a presenter is currently looking at. Drives which controls the
 * bar offers.
 */
type Surface =
  | 'home'
  | 'care-roster'
  | 'patient-overview'
  | 'patient-labs'
  | 'none';

type ControlKey = 'home' | 'roster' | 'claims' | 'appStatus' | 'labs' | 'stage';

const CONTROLS_BY_SURFACE: Record<Surface, ControlKey[]> = {
  home: ['home'],
  'care-roster': ['roster'],
  'patient-overview': ['claims', 'appStatus'],
  'patient-labs': ['labs', 'stage'],
  none: [],
};

/**
 * Resolves route + reported patient tab to a surface. The patient tab comes
 * through context because it's state internal to `PatientProfile`; every other
 * profile tab (Progress, Appointment, Pharmacy) has no prototype states of its
 * own, so it falls through to 'none'.
 */
function resolveSurface(pathname: string, activeTab: string | null): Surface {
  if (pathname.startsWith('/provider-home/patient/')) {
    if (activeTab === 'Overview') return 'patient-overview';
    if (activeTab === 'Labs & Documents') return 'patient-labs';
    return 'none';
  }
  if (pathname === '/provider-home') return 'home';
  if (pathname === '/hc-dashboard') return 'care-roster';
  return 'none';
}

function Dropdown({ label, items, value, onSelect }: {
  label: string;
  items: { value: string; label: string }[];
  value: string;
  onSelect: (v: string) => void;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      <span style={{
        fontSize: LABEL_FONT_SIZE, fontWeight: LABEL_FONT_WEIGHT,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap',
      }}>{label}</span>
      {/* Native select: keyboard + screen-reader behaviour for free, and the
          OS popup renders above the app without any portal/z-index work. The
          chevron is ours; `appearance: none` drops the platform one. */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <select
          value={value}
          onChange={e => onSelect(e.target.value)}
          style={{
            appearance: 'none', WebkitAppearance: 'none',
            padding: '3px 22px 3px 8px', borderRadius: 5, cursor: 'pointer',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: 'white',
            // `select`/`button` don't inherit font-family from the document, so
            // without this they'd render in the UA default rather than the
            // repo's CircularXX (set on `body` in index.css).
            fontFamily: 'inherit',
            fontSize: VALUE_FONT_SIZE, fontWeight: VALUE_FONT_WEIGHT,
            lineHeight: 1.25,
            transition: 'background 0.12s ease, border-color 0.12s ease',
          }}
        >
          {items.map(item => (
            // Options render in the OS popup, which ignores the dark styling
            // above — set an explicit dark pair so they aren't white-on-white.
            <option key={item.value} value={item.value} style={{ color: '#043338', background: 'white' }}>
              {item.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={12}
          color="rgba(255,255,255,0.5)"
          style={{ position: 'absolute', right: 6, pointerEvents: 'none' }}
        />
      </div>
    </label>
  );
}

export function PrototypeBar() {
  const { pathname } = useLocation();
  const {
    journeyStage, setJourneyStage,
    labStatus, setLabStatus,
    appStatus, setAppStatus,
    claimsState, setClaimsState,
    homeState, setHomeState,
    rosterState, setRosterState,
    activeTab,
    expanded, isDirty, reset,
  } = usePrototypeState();

  if (!PROTOTYPE_BAR_ENABLED) return null;

  const visible = CONTROLS_BY_SURFACE[resolveSurface(pathname, activeTab)];

  return (
    // Height-animated wrapper rather than a conditional render, so expanding
    // and collapsing slides instead of snapping. `flexShrink: 0` keeps it out
    // of the fight for vertical space with the app below.
    <div
      style={{
        height: expanded ? BAR_HEIGHT : 0,
        flexShrink: 0,
        overflow: 'hidden',
        background: BAR_BG,
        borderBottom: expanded ? '1px solid rgba(255,255,255,0.08)' : 'none',
        // `visibility` keeps the collapsed dropdowns out of the tab order and
        // the a11y tree. It animates discretely, so on collapse it holds
        // `visible` for the duration and the row still slides shut.
        visibility: expanded ? 'visible' : 'hidden',
        transition: 'height 0.18s ease, visibility 0.18s ease',
      }}
      aria-hidden={!expanded}
    >
      {/* The row can't wrap without breaking the fixed height the collapse
          animates to, so on narrow windows it scrolls sideways instead of
          clipping the last control off the end. */}
      <div style={{
        height: BAR_HEIGHT,
        display: 'flex', alignItems: 'center',
        padding: '0 16px', gap: 16,
        overflowX: 'auto', overflowY: 'hidden',
      }}>
        {visible.includes('home') && (
          <Dropdown
            label="State"
            items={HOME_STATES}
            value={homeState}
            onSelect={v => setHomeState(v as HomeState)}
          />
        )}
        {visible.includes('roster') && (
          <Dropdown
            label="Panel"
            items={ROSTER_STATES}
            value={rosterState}
            onSelect={v => setRosterState(v as RosterState)}
          />
        )}
        {visible.includes('claims') && (
          <Dropdown
            label="Claims Insights"
            items={CLAIMS_STATES}
            value={claimsState}
            onSelect={v => setClaimsState(v as ClaimsInsightsState)}
          />
        )}
        {visible.includes('appStatus') && (
          <Dropdown
            label="App Status"
            items={APP_STATUSES}
            value={appStatus}
            onSelect={v => setAppStatus(v as AppStatus)}
          />
        )}
        {visible.includes('labs') && (
          <Dropdown
            label="Labs"
            items={LAB_STATUSES}
            value={labStatus ?? 'none'}
            onSelect={v => setLabStatus(v === 'none' ? null : (v as LabOrderStatus))}
          />
        )}
        {visible.includes('stage') && (
          <Dropdown
            label="Stage"
            items={STAGES}
            value={journeyStage}
            onSelect={v => setJourneyStage(v as JourneyStage)}
          />
        )}

        {/* Without this the bar would open to a blank strip on surfaces with no
            states of their own, reading as broken rather than as "nothing to
            set here". */}
        {visible.length === 0 && (
          <span style={{
            fontSize: VALUE_FONT_SIZE, fontWeight: VALUE_FONT_WEIGHT,
            color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap',
          }}>
            No prototype states for this view
          </span>
        )}

        <div style={{ flex: 1 }} />

        <button
          type="button"
          onClick={reset}
          disabled={!isDirty}
          title="Return every control to its default"
          style={{
            display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
            padding: '3px 8px', borderRadius: 5,
            background: 'transparent', border: '1px solid rgba(255,255,255,0.14)',
            color: isDirty ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.25)',
            cursor: isDirty ? 'pointer' : 'default',
            fontFamily: 'inherit',
            fontSize: VALUE_FONT_SIZE, fontWeight: VALUE_FONT_WEIGHT,
            transition: 'color 0.12s ease',
          }}
        >
          <RotateCcw size={11} />
          Reset
        </button>
      </div>
    </div>
  );
}
